import {inject, Injectable} from '@angular/core';
import * as Sentry from "@sentry/angular";
import {AppVersionService, LocalStorage} from '../utils/utils.module';

import { CB_CORE_ENV } from '../../environment/cb-core-env.token';

@Injectable()
export class SentryService {
  offlineEventsKey: string = '_cb_offline_events';
  previousRequestResponseKey: string = '_cb_previous_response';
  previousRequestResponse: string | undefined;
  protected readonly appVersion = inject(AppVersionService).getAppVersion();
  private env = inject(CB_CORE_ENV);

  constructor(
    private _localStorage: LocalStorage
  ) { }

  init() {
    const DSN = this.env.sentryUrl;
    const releaseVersion = this.appVersion;

    const sentryOptions: any = {
      dsn: DSN,
      defaultIntegrations: false,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Additional SDK configuration goes in here, for example:
          maskAllText: true,
          blockAllMedia: true,
        })
      ],
      replaysSessionSampleRate: 0.1, // Capture 10% of all sessions
      // replaysOnErrorSampleRate disabled (2026-09-06): keeps rrweb's Mirror recording
      // continuously in the background for every session ("buffer mode") to be ready to
      // send on error, and its idNodeMap never prunes removed/detached DOM nodes for the
      // life of the recording -- confirmed via heap-snapshot retainer trace to be the
      // cause of an unbounded memory leak on heavy client-side route navigation (e.g.
      // pools list <-> pool detail). See DECISIONS.md, 2026-09-06.
      replaysOnErrorSampleRate: 0,
      tracesSampleRate: 0.1, // 50%
      profilesSampleRate: 1,
      ignoreErrors: [
        /<unlabeled event>/, /Unknown/, /not implemented/, /minTimeBetweenSessions/, /minTimeBetweenSessions/
      ],
      tracePropagationTargets: [
        "localhost",
        /^https:\/\/colossusbets\.com\/api/,
        /^https:\/\/staging-colossusbets\.com\/api/,
        /^https:\/\/dev-preview.staging-colossusbets\.com\/api/,
        /^https:\/\/wallet.staging-colossusbets\.com\/wallet_api/,
        /^https:\/\/colossusbets\.com\/wallet_api/
      ],
      denyUrls: [
        /graph\.facebook\.com/i,
        /connect\.facebook\.net\/en_US\/all\.js/i,
        /api\.hubapi\.com/i,
        /o2\.mouseflow\.com/i,
        /extensions\//i,
        /^chrome:\/\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      ]
    };

    // Add release management for sentry
    if (releaseVersion) {
      sentryOptions.release = releaseVersion;
    }

    Sentry.init(sentryOptions);

    this.setTags();

    this.checkOfflineEvents();
  }

  setTags() {
    Sentry.getCurrentScope().setTag('stack', 'FRONTEND');
  }

  logError(error: any, errorInfo: any, tags?: any): void {
    Sentry.withScope(scope => {
      // 1. Handle Tags (Optional)
      if (tags && typeof tags === 'object') {
        Object.keys(tags).forEach(key => {
          // Sentry tags should be strings
          scope.setTag(key, String(tags[key]));
        });
      }

      // 2. Handle Extra Info
      if (errorInfo && typeof errorInfo === 'object') {
        Object.keys(errorInfo).forEach(key => {
          scope.setExtra(key, errorInfo[key]);
        });
      } else {
        scope.setExtra('context', errorInfo);
      }

      Sentry.captureException(error);
    });
  }

  logMessage(message: string, data?: any, tags?: any, level: Sentry.SeverityLevel = 'info'): void {
    Sentry.withScope(scope => {
      // Set severity level
      scope.setLevel(level);

      // Handle Tags (Optional)
      if (tags && typeof tags === 'object') {
        Object.keys(tags).forEach(key => {
          scope.setTag(key, String(tags[key]));
        });
      }

      // Handle Extra Data
      if (data && typeof data === 'object') {
        Object.keys(data).forEach(key => {
          scope.setExtra(key, data[key]);
        });
      } else if (data) {
        scope.setExtra('data', data);
      }

      Sentry.captureMessage(message, level);
    });
  }

  savePreviousRequestResponse(response: string) {
    if (!response) return;
    this.previousRequestResponse = JSON.stringify(response);
    this._localStorage.set(this.previousRequestResponseKey, this.previousRequestResponse);
  }

  getPreviousResponse() {
    return this.previousRequestResponse;
  }

  sendEvent(error: any, hint?: any): void {
    if (!error) return;
    const isConnected = window.navigator.onLine;

    if (isConnected) {
      Sentry.captureEvent(error, hint);
    } else {
      let newOfflineEvents = [error];
      const savedEvents = this._localStorage.get(this.offlineEventsKey);
      newOfflineEvents = newOfflineEvents.concat(savedEvents);
      this._localStorage.set(this.offlineEventsKey, newOfflineEvents);
    }
  }

  checkOfflineEvents() {
    this._localStorage.get(this.offlineEventsKey).then(res => {
        const savedEvents = res;
        if (savedEvents) {
          for (let i = 0; i < savedEvents.length - 1; i++) {
            this.sendEvent(savedEvents[i]);
            this.deleteSavedEvent(savedEvents[i]);
          }
        }
    })
  }

  deleteSavedEvent(error: any) {
    if (!error) return;
    this._localStorage.get(this.offlineEventsKey).then(res => {
        const savedEvents = res;
        const newOfflineEvents = savedEvents.splice(savedEvents.indexOf(error), 1);
        this._localStorage.set(this.offlineEventsKey, newOfflineEvents).then(res => {});
    })
  }
}
